/* ==========================================================================
   AT Ajax Search v7 — two-column predictive search dropdown
   Uses Shopify's native /search/suggest.json. No jQuery required.

   Changes from v6:
   - The panel is sized against window.visualViewport instead of
     window.innerHeight. innerHeight does NOT shrink when the on-screen
     keyboard opens, so the panel was being sized to the full screen and its
     bottom — including the "Afficher tous les résultats" footer — ended up
     underneath the keyboard.
   - visualViewport.offsetTop is honoured too, for when the page is pinch-
     zoomed or the browser shifts the visual viewport.
   - If the usable height is very small, the panel is pinned to the top of the
     visible area so the footer still lands on screen.

   Earlier fixes retained:
   - Delegated listeners (Electro clones the header for the sticky/mobile bar)
   - Own panel per input, parked on <body>, fixed-positioned on all viewports
   - Panel never closes from scrolling, inside or outside it
   - resources[limit_scope]=each so each resource type gets a full allowance
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = {
    inputSelector:   '.bc-product-search',
    wrapperSelector: '.result-ajax-search',

    mobileBreakpoint: 991,   // keep in sync with the CSS media queries
    gutter:           8,     // px from the screen edge when floating (mobile)
    offset:           6,     // px gap below the input
    minPanelHeight:   200,   // px, floor for the panel
    desktopMinWidth:  560,   // px, so a narrow search form still gets a usable
                             // panel; capped to the viewport
    minTop:           8,     // px, how close to the top edge it may pin

    minChars:        2,
    debounceMs:      250,

    productLimit:    9,
    queryLimit:      4,
    collectionLimit: 4,

    // Excludes product descriptions from matching on purpose.
    // Valid: author, body, product_type, tag, title,
    //        variants.barcode, variants.sku, variants.title, vendor
    fields: 'title,product_type,tag,vendor,variants.sku,variants.title',

    currency: 'DT',

    labels: {
      suggestions: 'Suggestions',
      collections: 'Collections',
      products:    'Produits',
      soldOut:     'Épuisé',
      empty:       'Aucun résultat pour',
      emptyHint:   'Vérifiez l\u2019orthographe ou essayez un terme plus court.',
      viewAll:     'Afficher tous les résultats',
      viewAllN:    'Afficher tous les {n} articles',
      error:       'La recherche est momentanément indisponible.'
    }
  };

  /* ---------------------------------------------------------------- utils */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlight(text, term) {
    var safeText = escapeHtml(text);
    var words = term.trim().split(/\s+/).filter(function (w) { return w.length > 1; });
    if (!words.length) return safeText;
    var pattern = words.map(function (w) { return escapeRegex(escapeHtml(w)); }).join('|');
    try {
      return safeText.replace(new RegExp('(' + pattern + ')', 'gi'), '<em class="ats-mark">$1</em>');
    } catch (e) {
      return safeText;
    }
  }

  function toNumber(m) {
    if (m === null || m === undefined) return NaN;
    var s = String(m).replace(/[^\d.,]/g, '');
    var at = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    if (at === -1) return parseFloat(s);
    return parseFloat(s.slice(0, at).replace(/[.,]/g, '') + '.' + s.slice(at + 1).replace(/[.,]/g, ''));
  }

  function money(v) { return escapeHtml(v) + ' ' + CONFIG.currency; }

  function discountPercent(price, compareAt) {
    var p = toNumber(price), c = toNumber(compareAt);
    if (!isFinite(p) || !isFinite(c) || c <= 0 || p >= c) return 0;
    return Math.round((1 - p / c) * 100);
  }

  function isMobile() {
    return window.innerWidth <= CONFIG.mobileBreakpoint;
  }

  function closestMatch(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  // The slice of screen actually visible. With the keyboard open this is much
  // shorter than window.innerHeight, which does not change on iOS or Android.
  function viewport() {
    var vv = window.visualViewport;
    if (vv) {
      return {
        top: vv.offsetTop || 0,
        height: vv.height,
        width: vv.width
      };
    }
    return { top: 0, height: window.innerHeight, width: window.innerWidth };
  }

  /* ------------------------------------------------------------ rendering */

  function productCard(product, term) {
    var pct = discountPercent(product.price, product.compare_at_price_max);
    var img = (product.featured_image && product.featured_image.url) || product.image || '';
    var alt = (product.featured_image && product.featured_image.alt) || product.title || '';

    var priceBlock;
    if (product.available === false) {
      priceBlock = '<span class="ats-card__price ats-card__price--out">' +
                   escapeHtml(CONFIG.labels.soldOut) + '</span>';
    } else if (pct > 0) {
      priceBlock = '<span class="ats-card__price ats-card__price--sale">' + money(product.price) + '</span>' +
                   '<s class="ats-card__was">' + money(product.compare_at_price_max) + '</s>';
    } else {
      priceBlock = '<span class="ats-card__price">' + money(product.price) + '</span>';
    }

    return '' +
      '<a class="ats-card" href="' + escapeHtml(product.url) + '" data-ats-item>' +
        '<span class="ats-card__media">' +
          (pct > 0 ? '<span class="ats-card__badge">-' + pct + '%</span>' : '') +
          (img
            ? '<img class="ats-card__img" src="' + escapeHtml(img) + '" alt="' + escapeHtml(alt) + '" loading="lazy" width="120" height="120">'
            : '<span class="ats-card__img ats-card__img--empty" aria-hidden="true"></span>') +
        '</span>' +
        '<span class="ats-card__title">' + highlight(product.title, term) + '</span>' +
        '<span class="ats-card__prices">' + priceBlock + '</span>' +
      '</a>';
  }

  function sideList(queries, collections, term) {
    var html = '';
    if (queries.length) {
      html += '<p class="ats-col__heading">' + escapeHtml(CONFIG.labels.suggestions) + '</p><ul class="ats-list">';
      queries.forEach(function (q) {
        var text = q.text || q;
        html += '<li><a class="ats-list__link" href="/search?q=' + encodeURIComponent(text) +
                '" data-ats-item>' + highlight(text, term) + '</a></li>';
      });
      html += '</ul>';
    }
    if (collections.length) {
      html += '<p class="ats-col__heading ats-col__heading--sub">' +
              escapeHtml(CONFIG.labels.collections) + '</p><ul class="ats-list">';
      collections.forEach(function (c) {
        html += '<li><a class="ats-list__link" href="' + escapeHtml(c.url) +
                '" data-ats-item>' + highlight(c.title, term) + '</a></li>';
      });
      html += '</ul>';
    }
    return html;
  }

  function render(wrapper, data, term, totalCount) {
    var products    = data.products || [];
    var queries     = data.queries || [];
    var collections = data.collections || [];

    if (!products.length && !queries.length && !collections.length) {
      wrapper.innerHTML =
        '<div class="ats ats--empty">' +
          '<p class="ats-empty__title">' + escapeHtml(CONFIG.labels.empty) +
            ' \u201c' + escapeHtml(term) + '\u201d</p>' +
          '<p class="ats-empty__hint">' + escapeHtml(CONFIG.labels.emptyHint) + '</p>' +
        '</div>';
      return;
    }

    var hasSide = queries.length || collections.length;
    var footerLabel = (typeof totalCount === 'number' && isFinite(totalCount))
      ? CONFIG.labels.viewAllN.replace('{n}', totalCount)
      : CONFIG.labels.viewAll;

    wrapper.innerHTML = '' +
      '<div class="ats' + (hasSide ? '' : ' ats--no-side') + '">' +
        (hasSide ? '<div class="ats-col ats-col--side">' + sideList(queries, collections, term) + '</div>' : '') +
        '<div class="ats-col ats-col--main">' +
          '<p class="ats-col__heading">' + escapeHtml(CONFIG.labels.products) + '</p>' +
          '<div class="ats-grid">' + products.map(function (p) { return productCard(p, term); }).join('') + '</div>' +
        '</div>' +
        '<a class="ats-footer" href="/search?q=' + encodeURIComponent(term) +
          '" data-ats-item>' + escapeHtml(footerLabel) + '</a>' +
      '</div>';
  }

  /* ------------------------------------------------------------ data layer */

  var cache = Object.create(null);

  function fetchSuggestions(term, signal) {
    // limit_scope=each gives every resource type its own allowance. Without it
    // the limit is shared across all three types, which is why only ~3 products
    // came back. 10 is Shopify's per-type maximum; the slice() calls below trim
    // to the per-column limits in CONFIG.
    var params = '?q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,query' +
      '&resources[limit]=10' +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + encodeURIComponent(CONFIG.fields);

    return fetch('/search/suggest.json' + params, {
      signal: signal,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var res = (json.resources && json.resources.results) || {};
        return {
          products:    (res.products || []).slice(0, CONFIG.productLimit),
          queries:     (res.queries || []).slice(0, CONFIG.queryLimit),
          collections: (res.collections || []).slice(0, CONFIG.collectionLimit)
        };
      });
  }

  function fetchCount(term, signal) {
    return fetch('/search?q=' + encodeURIComponent(term) + '&type=product&view=count', { signal: signal })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (text) {
        if (!text) return null;
        var n = parseInt(String(text).replace(/[^\d]/g, ''), 10);
        return isFinite(n) ? n : null;
      })
      .catch(function () { return null; });
  }

  /* ------------------------------------------------------- per-input state */

  var states = typeof WeakMap === 'function' ? new WeakMap() : null;
  var fallbackStates = [];

  function newState(input) {
    return {
      input: input,
      wrapper: null,
      controller: null,
      timer: null,
      cursor: -1
    };
  }

  function getState(input) {
    if (states) {
      if (!states.has(input)) states.set(input, newState(input));
      return states.get(input);
    }
    for (var i = 0; i < fallbackStates.length; i++) {
      if (fallbackStates[i].input === input) return fallbackStates[i];
    }
    var s = newState(input);
    fallbackStates.push(s);
    return s;
  }

  // Each input gets its own panel, parked on <body>. The theme's own
  // .result-ajax-search containers are left alone and permanently hidden.
  function resolveWrapper(input) {
    var state = getState(input);
    if (state.wrapper && state.wrapper.isConnected) return state.wrapper;

    var panel = document.createElement('div');
    panel.className = 'result-ajax-search ats-floating';
    panel.style.display = 'none';
    document.body.appendChild(panel);

    state.wrapper = panel;
    return panel;
  }

  /* ----------------------------------------------------------- positioning */

  // Fixed positioning against the input's live rectangle, sized to the VISIBLE
  // viewport. Keyboard open means a much shorter usable height; measuring with
  // window.innerHeight would push the footer under the keyboard.
  function place(input, wrapper) {
    var r = input.getBoundingClientRect();
    var vp = viewport();

    var visibleTop = vp.top;
    var visibleBottom = vp.top + vp.height;

    var top = Math.round(r.bottom + CONFIG.offset);

    // Never above the visible area, never so low the panel has no room
    var lowestTop = visibleBottom - CONFIG.minPanelHeight - CONFIG.gutter;
    top = Math.max(visibleTop + CONFIG.minTop, Math.min(top, lowestTop));

    var available = visibleBottom - top - CONFIG.gutter;
    if (available < CONFIG.minPanelHeight) {
      // Very little room (small phone, keyboard up): take the visible area
      top = visibleTop + CONFIG.minTop;
      available = vp.height - CONFIG.minTop - CONFIG.gutter;
    }

    wrapper.style.position = 'fixed';
    wrapper.style.top = Math.round(top) + 'px';
    wrapper.style.bottom = 'auto';
    wrapper.style.maxHeight = Math.round(Math.max(CONFIG.minPanelHeight, available)) + 'px';

    if (isMobile()) {
      wrapper.style.left = CONFIG.gutter + 'px';
      wrapper.style.right = CONFIG.gutter + 'px';
      wrapper.style.width = 'auto';
      return;
    }

    // Desktop: match the search form, widening a narrow one to stay usable
    var anchor = input.closest('form') || input.closest('.searchbox') || input;
    var ar = anchor.getBoundingClientRect();
    var vw = vp.width || window.innerWidth;

    var width = Math.max(ar.width, CONFIG.desktopMinWidth);
    width = Math.min(width, vw - CONFIG.gutter * 2);

    var left = ar.left;
    if (left + width > vw - CONFIG.gutter) left = vw - CONFIG.gutter - width;
    left = Math.max(CONFIG.gutter, left);

    wrapper.style.left = Math.round(left) + 'px';
    wrapper.style.right = 'auto';
    wrapper.style.width = Math.round(width) + 'px';
  }

  /* --------------------------------------------------------------- actions */

  var openInput = null;   // whichever input owns the visible panel

  function show(input, wrapper) {
    place(input, wrapper);
    wrapper.classList.add('is-open');
    wrapper.style.display = 'flex';
    openInput = input;

    // The keyboard animates open after focus; re-measure once it has settled
    setTimeout(function () {
      if (openInput === input && wrapper.classList.contains('is-open')) {
        place(input, wrapper);
      }
    }, 300);
  }

  function hide(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('is-open');
    wrapper.style.display = 'none';
  }

  function hideAll() {
    document.querySelectorAll('.result-ajax-search').forEach(hide);
    openInput = null;
  }

  function setLoading(input, on) {
    var box = input.closest('.searchbox');
    if (box) box.classList.toggle('s-loading', !!on);
  }

  function run(input, term) {
    var state = getState(input);
    var wrapper = resolveWrapper(input);
    if (!wrapper) return;

    if (cache[term]) {
      render(wrapper, cache[term].data, term, cache[term].count);
      show(input, wrapper);
      return;
    }

    if (state.controller) state.controller.abort();
    state.controller = new AbortController();
    var signal = state.controller.signal;
    setLoading(input, true);

    Promise.all([fetchSuggestions(term, signal), fetchCount(term, signal)])
      .then(function (out) {
        cache[term] = { data: out[0], count: out[1] };
        render(wrapper, out[0], term, out[1]);
        show(input, wrapper);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        wrapper.innerHTML = '<div class="ats ats--empty"><p class="ats-empty__title">' +
          escapeHtml(CONFIG.labels.error) + '</p></div>';
        show(input, wrapper);
      })
      .then(function () { setLoading(input, false); });
  }

  function moveCursor(input, delta) {
    var state = getState(input);
    var wrapper = state.wrapper;
    if (!wrapper) return;

    var list = Array.prototype.slice.call(wrapper.querySelectorAll('[data-ats-item]'));
    if (!list.length) return;

    if (state.cursor >= 0 && list[state.cursor]) {
      list[state.cursor].classList.remove('is-active');
    }
    state.cursor += delta;
    if (state.cursor < 0) state.cursor = list.length - 1;
    if (state.cursor >= list.length) state.cursor = 0;

    list[state.cursor].classList.add('is-active');
    list[state.cursor].scrollIntoView({ block: 'nearest' });
  }

  /* ------------------------------------------------- delegated event wiring */

  document.addEventListener('input', function (e) {
    var input = closestMatch(e.target, CONFIG.inputSelector);
    if (!input) return;

    var state = getState(input);
    var term = input.value.trim();
    state.cursor = -1;

    input.setAttribute('autocomplete', 'off');

    clearTimeout(state.timer);

    if (term.length < CONFIG.minChars) {
      hide(resolveWrapper(input));
      if (state.controller) state.controller.abort();
      setLoading(input, false);
      return;
    }

    state.timer = setTimeout(function () { run(input, term); }, CONFIG.debounceMs);
  });

  document.addEventListener('keydown', function (e) {
    var input = closestMatch(e.target, CONFIG.inputSelector);
    if (!input) return;

    var state = getState(input);
    var wrapper = state.wrapper;
    if (!wrapper || !wrapper.classList.contains('is-open')) return;

    if (e.key === 'ArrowDown') { e.preventDefault(); moveCursor(input, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveCursor(input, -1); }
    else if (e.key === 'Escape') { hide(wrapper); input.blur(); }
    else if (e.key === 'Enter') {
      var list = Array.prototype.slice.call(wrapper.querySelectorAll('[data-ats-item]'));
      if (state.cursor >= 0 && list[state.cursor]) {
        e.preventDefault();
        window.location.href = list[state.cursor].getAttribute('href');
      }
    }
  });

  document.addEventListener('focusin', function (e) {
    var input = closestMatch(e.target, CONFIG.inputSelector);
    if (!input) return;

    var wrapper = resolveWrapper(input);

    document.querySelectorAll('.result-ajax-search').forEach(function (w) {
      if (w !== wrapper) hide(w);
    });

    if (input.value.trim().length >= CONFIG.minChars && wrapper.innerHTML.trim()) {
      show(input, wrapper);
    }
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.result-ajax-search')) return;
    if (e.target.closest(CONFIG.inputSelector)) return;
    hideAll();
  });

  /* ------------------------------------------------ keeping the panel placed */

  // While a finger or pointer is down inside the panel the user is scrolling
  // it; repositioning then would fight the gesture.
  var interacting = false;

  function beginInteraction(e) {
    if (closestMatch(e.target, '.result-ajax-search')) interacting = true;
  }

  function endInteraction() {
    setTimeout(function () { interacting = false; }, 350);
  }

  document.addEventListener('touchstart', beginInteraction, { passive: true });
  document.addEventListener('touchend', endInteraction, { passive: true });
  document.addEventListener('touchcancel', endInteraction, { passive: true });
  document.addEventListener('pointerdown', beginInteraction, { passive: true });
  document.addEventListener('pointerup', endInteraction, { passive: true });

  // Only ever moves the panel — never hides it.
  var frame = null;

  function reposition() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      if (!openInput || interacting) return;

      var state = getState(openInput);
      if (!state.wrapper || !state.wrapper.classList.contains('is-open')) return;

      place(openInput, state.wrapper);
    });
  }

  window.addEventListener('scroll', reposition, { passive: true, capture: true });
  window.addEventListener('resize', reposition);
  window.addEventListener('orientationchange', function () {
    setTimeout(reposition, 150);
  });

  // The keyboard opening/closing shows up here, not on window resize
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', reposition);
    window.visualViewport.addEventListener('scroll', reposition);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideAll);
  } else {
    hideAll();
  }
})();